import { useResolvedAccessLevel } from '../../components/PermissionsContainer';

/**
 * Component id for the invoice-approval gate.
 *
 * V1 gated the Invoicing tab's "Approve" checkbox on a field ACL over the ORDER's
 * `approvedby` (lib_editrecord.pl:1583 — `getACL($acl_db, get_dbTableACL('approvedby'),
 * 'approvedby', '')`), resolved independently of whatever opened the LC-only order
 * fields. That ACL was never ported: the toggle borrowed `order-lc-fields`, which
 * welded "may approve an invoice" to "may edit Port of Origin / Discharge / DC / Dept"
 * — two different jobs and two different audiences. Invoice clerks approve and do not
 * touch LC fields; a Dispatch Manager holds the LC fields and picked up approval for
 * free. This restores the split, the same way `hazmat-approval` and `oog-approval` did
 * for their checkboxes (useCanApproveHazmat.ts, useCanApproveOogPermit.ts).
 *
 * ORDER-SIDE ONLY. On a TEMPLATE the approval switch stays gated by the template-edit
 * permission (useCanEditTemplateFields) and this component has no say — same rule as
 * InvoiceWriteGate and OrderLcFieldGroup, which render bare on templates rather than
 * behind an order-only permission the template editor may not hold.
 */
export const INVOICE_APPROVAL_COMPONENT_ID = 'invoice-approval';
export const INVOICE_APPROVAL_COMPONENT_NAME = 'Pro Invoicing — Invoice Approval';

/**
 * Whether the current user may (un)approve an ORDER's invoice.
 *
 * Resolved from the admin-configured component permission crossed with the user's
 * Azure AD groups. Ships **defaultDisabled**: with no grant the checkbox renders
 * read-only while the approver name and timestamp stay visible, so the approval STATE
 * is never hidden from someone who merely may not change it.
 *
 * >>> Migration 498 is not optional, and must land with the deploy, not after. <<<
 * It seeds this component from whatever currently holds `order-lc-fields` >= 2 in that
 * schema, plus GWI360-InvoiceClerk. On a schema where it has not run, defaultDisabled
 * means the checkbox goes read-only for EVERY user — including the 100 who can approve
 * today.
 *
 * Server-side enforced too, in both places a single-pro save can happen: the
 * `approvedby` arm of /orders/save-order-details and the /orders/change-all/apply
 * fan-out of the same column.
 */
export const useCanApproveInvoice = (): boolean =>
  useResolvedAccessLevel(INVOICE_APPROVAL_COMPONENT_ID, false, true) >= 2;
